import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { tap } from "rxjs/operators";

import { MainService } from "src/app/app.service";

export interface UserAccessItem {
  id: string;
  username: string;
  roles: Array<string>;
  updatedAt?: string;
  areaId?: string;
  areaName?: string;
  tribeId?: string;
  tribeName?: string;
  /** Marcação da linha no po-table. */
  $selected?: boolean;
}

export interface UsersAccessPage {
  items: Array<UserAccessItem>;
  total: number;
}

export interface UsersAccessSummary {
  total: number;
  noArea: number;
  noTribe: number;
  tribes: number;
  areas: number;
  areasWithoutTribe: number;
  superusers: number;
  byRole: Record<string, number>;
}

export interface UsersAccessFilters {
  q?: string;
  tribe?: Array<string>;
  area?: Array<string>;
  role?: Array<string>;
  noArea?: boolean;
  noTribe?: boolean;
  offset?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export interface BulkRolesPayload {
  usernames: Array<string>;
  add?: Array<string>;
  remove?: Array<string>;
}

@Injectable()
export class UsersAccessService {
  private readonly baseUrl = "/api/v2/users";

  constructor(
    private http: HttpClient,
    private mainService: MainService,
  ) {}

  private buildParams(filters: UsersAccessFilters = {}): HttpParams {
    let params = new HttpParams();
    const { tribe, area, role, ...scalars } = filters;

    (tribe ?? []).forEach((v) => (params = params.append("tribe", v)));
    (area ?? []).forEach((v) => (params = params.append("area", v)));
    (role ?? []).forEach((v) => (params = params.append("role", v)));

    Object.entries(scalars).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        return;
      }
      params = params.set(key, String(value));
    });

    return params;
  }

  list(filters: UsersAccessFilters = {}): Observable<UsersAccessPage> {
    return this.http.get<UsersAccessPage>(`${this.baseUrl}/access`, {
      headers: this.mainService.httpOptions.headers,
      params: this.buildParams(filters),
    });
  }

  summary(filters: UsersAccessFilters = {}): Observable<UsersAccessSummary> {
    return this.http.get<UsersAccessSummary>(`${this.baseUrl}/access/summary`, {
      headers: this.mainService.httpOptions.headers,
      params: this.buildParams(filters),
    });
  }

  exportCsv(filters: UsersAccessFilters = {}): Observable<string> {
    return this.http.get(`${this.baseUrl}/access/export`, {
      headers: this.mainService.httpOptions.headers,
      params: this.buildParams(filters),
      responseType: "text",
    });
  }

  bulkUpdateRoles(payload: BulkRolesPayload): Observable<{ updated: number; notFound: Array<string> }> {
    return this.http
      .patch<{ updated: number; notFound: Array<string> }>(`${this.baseUrl}/roles`, payload, this.mainService.httpOptions)
      .pipe(tap(() => this.refreshSessionIfSelf(payload.usernames)));
  }

  private refreshSessionIfSelf(usernames: Array<string>): void {
    const me = (this.mainService.userToken?.username ?? "").toLowerCase();
    if (me && usernames.some((u) => u.toLowerCase() === me)) {
      this.mainService.refreshSessionArea?.().subscribe();
    }
  }
}
